import { Link } from "react-router-dom";
import { Page } from "@/components/PublicLayout";
import { ContentBody } from "@/components/ContentBody";
import { ImportantNote } from "@/components/ImportantNote";
import { useBlocks, useCounselors } from "@/hooks/useContent";
import { FreelyGiven } from "@/components/FreelyGiven";
import { t } from "@/lib/i18n";

const Block = ({ block, testId }) => (block ? <ContentBody body={block.body} className="" data-testid={testId} /> : null);

export function WhoCanReceiveCarePage() {
  const { data: blocks } = useBlocks();
  const b = blocks?.["who.body"];
  const gt = blocks?.["who.growth_track"];
  const ny = blocks?.["who.not_yet"];
  return (
    <Page title={b?.title || "Who Can Receive Care"} testId="who-page">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7 space-y-8">
          <Block block={b} />
          {gt && (
            <p className="prose-care">
              <a href={gt.url} className="link" target="_blank" rel="noopener noreferrer" data-testid="growth-track-link">{gt.title}</a>
              {" "}— {gt.body}
            </p>
          )}
          {ny && <p className="surface max-w-2xl p-5 text-ink-900" data-testid="not-there-yet">{ny.body}</p>}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/sign-up" className="btn-primary" data-testid="who-get-care-button">{t("nav.getCare")}</Link>
            <Link to="/resources" className="btn-secondary">{t("nav.resources")}</Link>
          </div>
          <FreelyGiven variant="card" testId="who-giving" />
        </div>
        <div className="lg:col-span-5"><ImportantNote /></div>
      </div>
    </Page>
  );
}

export function OurCounselorsPage() {
  const { data: blocks } = useBlocks();
  const { data: counselors = [], isLoading } = useCounselors();
  const intro = blocks?.["counselors.intro"];
  return (
    <Page title={intro?.title || "Our Counselors"} lede={intro?.body} testId="counselors-page">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          {!isLoading && counselors.length === 0 && (
            <div className="surface max-w-2xl p-6" data-testid="counselors-empty">
              <p className="font-serif text-xl font-semibold">Profiles Are Coming</p>
              <p className="mt-2 text-ink-900">Counselor profiles appear here when each counselor chooses to share one.</p>
            </div>
          )}
          <ul className="space-y-6">
            {counselors.map((c) => (
              <li key={c.id} className="surface p-6" data-testid={`counselor-${c.id}`}>
                <h2 className="text-2xl">{c.name}</h2>
                <p className="text-ink-500">{[c.title, c.provider_type_label].filter(Boolean).join(" · ")}</p>
                {c.license_number && <p className="mt-2 text-sm">California license: {[c.license_type, c.license_number].filter(Boolean).join(" ")}</p>}
                {c.bio && <p className="mt-3 max-w-[60ch]">{c.bio}</p>}
                <p className="mt-3 text-sm text-ink-500">{[c.campuses?.length ? c.campuses.join(", ") : null, c.areas_of_focus?.length ? c.areas_of_focus.join(", ") : null, c.languages?.length ? c.languages.join(", ") : null].filter(Boolean).join(" · ")}</p>
                {!c.accepting_new && <p className="mt-2 text-sm font-semibold text-ink-500">Not accepting new cases right now</p>}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5"><ImportantNote /></div>
      </div>
    </Page>
  );
}

export function LifeCareClassesPage() {
  const { data: blocks } = useBlocks();
  const intro = blocks?.["classes.intro"];
  return (
    <Page title={intro?.title || "Life Care Classes"} lede={intro?.body} testId="classes-page">
      <div className="surface max-w-2xl p-6" data-testid="classes-empty">
        <p className="font-serif text-xl font-semibold">The Class Catalog Opens Soon</p>
        <p className="mt-2">Life Care class listings and registration arrive in Phase 7. Classes are open to everyone, and you will be able to register with your free account.</p>
        <Link to="/sign-up" className="btn-primary mt-5">{t("auth.signUpTitle")}</Link>
      </div>
    </Page>
  );
}

export function PrayWithUsPage() {
  const { data: blocks } = useBlocks();
  const body = blocks?.["pray.body"];
  const link = blocks?.["pray.link"];
  return (
    <Page title={body?.title || "Pray With Us"} testId="pray-page">
      <div className="max-w-2xl space-y-8">
        {body && <p className="prose-care text-lg">{body.body}</p>}
        {link && (
          <div className="surface p-6">
            <p className="font-serif text-xl font-semibold">{link.title}</p>
            <p className="mt-2">{link.body}</p>
            <a href={link.url} className="btn-primary mt-5" target="_blank" rel="noopener noreferrer" data-testid="prayer-request-link">Share A Prayer Request</a>
            <p className="help mt-3">This app does not collect prayer requests.</p>
          </div>
        )}
      </div>
    </Page>
  );
}
